import { Box, Typography, Card, Stack, ListItem, ListItemAvatar, Avatar, ListItemText, Table, TableBody, TableRow, TableCell, IconButton, Tooltip } from "@mui/material";
import { getFirestore, getDoc, doc } from "firebase/firestore";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import passportRequestConverter from "../../converters/passportRequestConverter";
import { Employee } from "../../types/Employee";
import { PassportRequest, PassportRequestStatus } from "../../types/PassportRequest";
import { queryEmployee } from "../../utils/queryEmployee";
import { Notification } from "../../types/Notification";
import { EnhancedTableCell } from "../EnhancedTableCell";
import { RunCircleRounded, MopedRounded, CheckCircleRounded } from "@mui/icons-material";
import { updatePassportRequest } from "../../utils/updatePassportRequest";
import { useSnackbar } from "notistack";
import { NotificationEmployeeHeading } from "./NotificationEmployeeHeading";

export const NotificationPassportRequest = (props: { notification: Notification }) => {
  const { notification } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [passportRequest, setPassportRequest] = useState<PassportRequest>();
  const [employee, setEmployee] = useState<Employee>();

  const db = getFirestore();

  const handleUpdatePassportRequest = (s: PassportRequestStatus) => {
    if (passportRequest) {
      setUpdating(true);
      updatePassportRequest(passportRequest, s).then(() => {
        let message = `Employee '${passportRequest.idNumber}' passport request has been update.`;
        if (s === 'Processing')
          message = `Employee '${passportRequest.idNumber}' passport request is now being processed.`;
        else if (s === 'Available')
          message = `Employee '${passportRequest.idNumber}' passport request is now ready for pickup.`;
        else if (s === 'Completed')
          message = `Employee '${passportRequest.idNumber}' passport request is now tagged as complete.`;
        else if (s === 'Cancelled')
          message = `Employee '${passportRequest.idNumber}' passport request is now cancelled.`;

        setPassportRequest({ ...passportRequest, status: s });
        enqueueSnackbar(message, { variant: 'success' });

        setUpdating(false);
      })
    }
  };

  useEffect(() => {
    // Get passport request
    if (notification.pathToDocument) {
      getDoc(doc(db, notification.pathToDocument).withConverter(passportRequestConverter)).then(qDocSnapshot => {
        if (qDocSnapshot.exists()) {
          setPassportRequest(qDocSnapshot.data());
        }
      });
    }
  }, []);

  useEffect(() => {
    if (passportRequest?.idNumber !== employee?.idNumber) {
      setLoading(true);
      if (passportRequest !== undefined) {
        queryEmployee(passportRequest.idNumber).then(e => {
          setEmployee(e);
          setLoading(false);
        });
      }
    }
  }, [passportRequest]);

  return (
    <Box>
      <Typography variant='h4' mb={1}>Passport Request</Typography>
      <Card>
        {loading ? (
          <Box p={1}>
            <Typography>Please wait...</Typography>
          </Box>
        ) : (
          <Box p={1}>
            <Stack direction='row' mb={2}>
              {employee && <NotificationEmployeeHeading employee={employee} />}
              <Stack direction='row' sx={{ alignItems: 'center' }}>
                <Tooltip title='Set as Processing'>
                  <IconButton onClick={() => handleUpdatePassportRequest('Processing')} color="warning" disabled={passportRequest?.status !== 'New' || updating}>
                    <RunCircleRounded />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Set as Ready for Pickup'>
                  <IconButton onClick={() => handleUpdatePassportRequest('Available')} color="secondary" disabled={(passportRequest?.status !== 'New' && passportRequest?.status !== 'Processing') || updating}>
                    <MopedRounded />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Set as Completed'>
                  <IconButton onClick={() => handleUpdatePassportRequest('Completed')} color="success" disabled={(passportRequest?.status === 'Completed' || passportRequest?.status === 'Cancelled') || updating}>
                    <CheckCircleRounded />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <Stack p={1}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <EnhancedTableCell>Date Requested</EnhancedTableCell>
                    <TableCell>{passportRequest ? DateTime.fromISO(passportRequest.dateRequested).toFormat('MMMM dd, yyyy hh:mm:ss a') : ''}</TableCell>
                  </TableRow>
                  <TableRow>
                    <EnhancedTableCell component="th">Type</EnhancedTableCell>
                    <TableCell>{passportRequest?.type}</TableCell>
                  </TableRow>
                  <TableRow>
                    <EnhancedTableCell component="th">Reason</EnhancedTableCell>
                    <TableCell>{passportRequest?.reason}</TableCell>
                  </TableRow>
                  <TableRow>
                    <EnhancedTableCell component="th">Status</EnhancedTableCell>
                    <TableCell>{passportRequest?.status}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Stack>
          </Box>
        )}
      </Card>
    </Box>
  );
};
